import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Send, Trash2, MessageCircle } from "lucide-react";
import api, { formatApiError, formatRelative } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Avatar, AvatarFallback } from "./ui/avatar";

/**
 * MediaComments – kommentartråd på bilde, video eller album.
 *
 * Props:
 *   targetType: "image" | "album"
 *   targetId: UUID på media/album
 *   ownerId: eier-UUID (for å vise slett-knapp riktig)
 *   compact: hvis true → mindre padding (passer i lightbox)
 */
export default function MediaComments({ targetType, targetId, ownerId, compact = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const listRef = useRef(null);

  const base = `/${targetType === "album" ? "albums" : "images"}/${targetId}/comments`;

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(base);
      setItems(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [base, targetId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(base, { body: text });
      setItems((prev) => [data, ...prev]);
      setTotal((t) => t + 1);
      setBody("");
      // scroll til topp så ny kommentar synes
      if (listRef.current) listRef.current.scrollTop = 0;
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const removeComment = async (id) => {
    if (!window.confirm("Slette denne kommentaren?")) return;
    try {
      await api.delete(`${base}/${id}`);
      setItems((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const canDelete = (c) =>
    user && (user.id === c.author?.id || user.id === ownerId || user.role === "admin" || user.role === "owner");

  return (
    <div className={`flex flex-col min-h-0 flex-1 ${compact ? "" : "border border-white/10 bg-[var(--noir-elev)]"}`}
         data-testid={`comments-${targetType}-${targetId}`}>
      {compact && (
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 shrink-0">
          <MessageCircle className="h-3.5 w-3.5 text-[var(--magenta)]" />
          <span className="font-meta text-[var(--cream-soft)]">Kommentarer · {total}</span>
        </div>
      )}
      {!compact && (
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 shrink-0">
          <MessageCircle className="h-3.5 w-3.5 text-[var(--magenta)]" />
          <span className="font-meta text-[var(--cream-soft)]">
            Kommentarer · {total}
          </span>
        </div>
      )}

      {/* Kommentar-input – øverst slik at det alltid er synlig */}
      {user ? (
        <form onSubmit={submit} className={`flex items-center gap-2 ${compact ? "px-3 py-3" : "px-5 py-4"} border-b border-white/10`}>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Skriv en kommentar …"
            maxLength={500}
            className="input-editorial flex-1"
            data-testid={`comment-input-${targetType}`}
          />
          <button
            type="submit"
            disabled={submitting || !body.trim()}
            className="h-9 w-9 rounded-full bg-[var(--magenta)] hover:bg-[var(--magenta-deep)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors shrink-0"
            data-testid={`comment-submit-${targetType}`}
            aria-label="Send kommentar"
          >
            <Send className="h-4 w-4 text-black" />
          </button>
        </form>
      ) : (
        <div className={`${compact ? "px-3 py-3" : "px-5 py-4"} border-b border-white/10 text-center font-meta text-[var(--cream-faint)]`}>
          <Link to="/login" className="text-[var(--magenta)] underline-offset-4 hover:underline">Logg inn</Link> for å kommentere
        </div>
      )}

      {error && (
        <div className="px-5 py-2 text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 text-xs border-b border-[var(--magenta)]/30 bg-[var(--magenta)]/10">
          {error}
        </div>
      )}

      {/* Kommentar-feed (scroller fritt innenfor wrapper-containerens høyde) */}
      <div
        ref={listRef}
        className={`flex-1 min-h-0 overflow-y-auto ${compact ? "" : "max-h-[400px]"}`}
        data-testid={`comments-list-${targetType}`}
      >
        {loading && items.length === 0 && (
          <div className="px-5 py-6 text-center font-meta text-[var(--cream-faint)]">Laster …</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-5 py-6 text-center font-meta text-[var(--cream-faint)]">
            Ingen kommentarer ennå.{user ? " Vær først ute!" : ""}
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="px-5 py-3 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors group" data-testid={`comment-${c.id}`}>
            <div className="flex gap-3">
              <Link to={`/profile/${c.author?.username}`} className="shrink-0">
                <Avatar className="h-8 w-8 rounded-sm border border-white/10">
                  {c.author?.avatar_url ? (
                    <img src={c.author.avatar_url} alt={c.author.username} className="h-full w-full object-cover" />
                  ) : (
                    <AvatarFallback className="bg-[var(--magenta)] text-black text-[10px] font-bold rounded-sm">
                      {(c.author?.username || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <Link
                    to={`/profile/${c.author?.username}`}
                    className="font-fraunces italic text-sm text-[var(--cream)] hover:text-[var(--magenta)]"
                  >
                    {c.author?.username || "ukjent"}
                  </Link>
                  {c.author?.is_creator && (
                    <span className="font-meta text-[8px] px-1 py-0.5 bg-[var(--cognac)]/30 text-[var(--cognac)] uppercase tracking-wider">
                      Creator
                    </span>
                  )}
                  <span className="font-meta text-[10px] text-[var(--cream-faint)]">
                    {formatRelative(c.created_at)}
                  </span>
                  {canDelete(c) && (
                    <button
                      onClick={() => removeComment(c.id)}
                      className="ml-auto opacity-0 group-hover:opacity-100 text-[var(--cream-faint)] hover:text-[var(--magenta)] transition-all"
                      data-testid={`comment-delete-${c.id}`}
                      aria-label="Slett kommentar"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <p className="font-manrope text-sm text-[var(--cream-soft)] mt-1 break-words whitespace-pre-wrap">
                  {c.body}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
